import img from "../assets/images/shutterstock_699442570-min.jpg";
import { useState } from "react";
import constants from "../constants/constants";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";

export default function Reports() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    interests: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, value]
        : prev.interests.filter((interest) => interest !== value),
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
      Name: formData.name,
      "Email Address": formData.email,
      "Client Type": formData.clientType,
      "Interested in:": formData.interests,
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      if (data?.success) {
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.reports();
  return (
    <>
      <SEO {...seoConfig} />
      <section className="bg-summit-background min-h-screen pt-40 pb-20">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.p
              variants={fadeUp}
              className="uppercase tracking-[5px] text-summit-highlight text-sm font-medium">
              Complimentary Resources
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="font-heading text-5xl md:text-6xl text-summit-primary mt-4">
              Free Real Estate Reports
            </motion.h1>

            <motion.div
              variants={fadeUp}
              className="w-20 h-1 bg-summit-highlight rounded-full mx-auto mt-6"></motion.div>

            <motion.p
              variants={fadeUp}
              className="max-w-3xl mx-auto mt-6 text-summit-muted leading-8">
              Receive valuable real estate reports designed to help buyers, sellers, and
              homeowners make informed decisions. Choose the topics you're interested in
              and we'll send them directly to your inbox.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid lg:grid-cols-[1.25fr_0.85fr] gap-12 items-start">
            <div className="bg-white rounded-3xl shadow-xl p-10">
              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Name*</label>
                  <input
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                  />
                </div>
                <div className="mb-5">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">Email*</label>
                  <input
                    required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                  />
                </div>
                <div className="mb-5">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">I am a*</label>
                  <select
                    required
                    name="clientType"
                    value={formData.clientType}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 outline-none transition focus:border-summit-highlight focus:bg-white"
                  >
                    <option value="">Please Select</option>
                    <option value="Buyer">Buyer</option>
                    <option value="Seller">Seller</option>
                    <option value="Renter">Renter</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-5">
                  <label className="block mb-2 text-sm uppercase tracking-wider text-slate-600">I am interested in:</label>
                  <div className="mt-3 grid md:grid-cols-2 gap-4">

                    {[
                      {
                        label: "Guides for Buying",
                        value: "Buying guides",
                      },
                      {
                        label: "Guides for Selling",
                        value: "Selling guides",
                      },
                      {
                        label: "Choosing a Realtor",
                        value: "Choosing realtor",
                      },
                      {
                        label: "Home Renovations",
                        value: "Renovations",
                      },
                    ].map((item) => (
                      <label
                        key={item.value}
                        className={`cursor-pointer rounded-2xl border p-3 transition-all duration-300 ${formData.interests.includes(item.value)
                          ? "border-summit-highlight bg-summit-highlight/10"
                          : "border-slate-200 bg-slate-50 hover:border-summit-highlight"
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            value={item.value}
                            checked={formData.interests.includes(item.value)}
                            onChange={handleCheckboxChange}
                            className="accent-[#BA9C5E] w-5 h-5"
                          />

                          <span className="font-medium text-summit-primary">
                            {item.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-luxury rounded-xl self-start mt-3"
                >
                  Request Reports
                </button>
              </form>
            </div>
            <div className="sticky top-40">

              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img
                  src={img}
                  alt="Real Estate"
                  className="w-full h-[620px] object-cover hover:scale-105 transition duration-700"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-luxury">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
